import React from "react";
import {
  Box,
  Button,
  Typography,
  makeStyles,
  IconButton,
} from "@material-ui/core";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";
import { getLang } from "app/feature/constants";
import { Skeleton } from "@material-ui/lab";
import moment from "moment";
import InstantWinDefaultBanner from "assets/img/instant_win_default_banner.png";

const useStyles = makeStyles((theme) => ({
  carouselWrapper: {
    position: "relative",
    minHeight: 520, 
    height: 520, 
  },
  container: {
    position: "relative",
    borderRadius: 16,
    minHeight: 520, 
    height: 520,  
    display: "flex",
    justifyContent: "space-between",
    scrollbarWidth: "thin",
    backgroundColor: "#ffffff",
    overflow: "hidden",
  },
  arrowButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    backgroundColor: "white",
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
    zIndex: 10,
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
    "&.Mui-disabled": {
      backgroundColor: "#e0e0e0",
      opacity: 0.5,
    },
  },
  leftArrow: {
    left: 8,
  },
  rightArrow: {
    right: 8,
  },
  label: {
    background:
      "linear-gradient(90deg, #6BDA8E 0%, #4D98D9 34.8%, #7077F7 62.65%, #CB62F6 100%);",
    padding: "2px 8px",
    borderRadius: 8,
    textTransform: "uppercase",
    fontWeight: 900,
    color: "white",
    width: "fit-content",
    textShadow: "-1px 0 #67D099, 1px 0 #949BC8, 1px 0 #AA80DF, 1px 0 #C065F6",
  },
  bannerContainer: {
    borderRadius: 16,
    padding: 16,
    position: "relative",
    background: "linear-gradient(95.48deg, #1A385E 50.93%, #461E70 100%)",
    color: "white",
    overflow: "hidden",
    minHeight: "100%", 
    display: "flex",    
    flexDirection: "column",
  },
  bannerDefaultImg: {
    position: "absolute",
    width: "50%",
    height: "90%",
    right: -50,
    bottom: -50,
    "& img": {
      height: "100%",
      width: "100%",
      objectFit: "contain",
    },
  },
  defaultThemeBannerCountdown: {
    position: "absolute",
    right: 0,
    top: 8,
    backgroundColor: "#FFFFFFCC",
    color: "black",
    padding: "2px 12px",
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  bannerImg: {
    width: "100%",
    height: "100%",
    borderRadius: 8,
    objectFit: "cover",
    background: "#eee",
    maxHeight: "320px",
    maxWidth: "300px",
    minHeight: "320px",
    minWidth: "300px", 
    display: "block",
  },
  bannerCountdown: {
    position: "absolute",
    right: 0,
    top: 10,
    backgroundColor: "#1A385EB2",
    color: "white",
    padding: "4px 12px",
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    backgroundColor: "#00000091",
    width: "100%",
    height: "100%",
    color: "white",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
  },
  button: {
    backgroundColor: "white",
    padding: 8,
    boxShadow: "0px 4px 0px 0px #A28EB7",
    borderRadius: 28,
    width: "100%",
    "&:hover": {
      backgroundColor: "white",
    },
  },
  indicators: {
    display: "flex",
    justifyContent: "center",
    gap: 8,
    marginTop: 16,
  },
  indicator: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "#D0D0D0",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&.active": {
      backgroundColor: "#7077F7",
      width: 24,
      borderRadius: 4,
    },
  },
}));

export default function InstantWinComponent({
  campaigns = [],
  lang,
  isLoading,
  countdowns = {}, // { [campaignId]: { day, hour, min, sec, status } }
  currentIndex = 0,
  setCurrentIndex = () => {},
  handlePrevious = () => {},
  handleNext = () => {},
}) {
  const classes = useStyles();

  const total = campaigns.length;

  const formatTwoDigits = (value) => (Number(value) < 10 ? `0${value}` : value);

  const renderCampaign = (instantWin, countdown) => {
  const joinOpenAt = instantWin?.join_open_at
    ? moment.utc(instantWin.join_open_at).local()
    : null;
  const joinCloseAt = instantWin?.join_close_at
    ? moment.utc(instantWin.join_close_at).local()
    : null;

  const isJoinStart = joinOpenAt ? moment().isAfter(joinOpenAt) : false;
  const isJoinEnd = joinCloseAt ? moment().isAfter(joinCloseAt) : false;
  const isEnded = instantWin?.end_at
    ? moment().isAfter(moment.utc(instantWin.end_at).local())
    : false;

  const cd = countdown || {
    day: 0,
    hour: 0,
    min: 0,
    sec: 0,
    status: "ended",
  };
  const showCountdown =
    (cd.day > 0 || cd.hour > 0 || cd.min > 0 || cd.sec > 0) &&
    !isEnded &&
    (cd.status === "starts_in" || cd.status === "ends_in");

  // Default banner
  if (!instantWin?.image_url) {
    return (
      <Box className={classes.bannerContainer}>
        <Box className={classes.label}>
          <Typography variant="h6" style={{ fontSize: 18 }}>
            <b>{getLang(lang, "label.INSTANT_WIN")}</b>
          </Typography>
        </Box>
        <Box style={{ width: "60%" }} mt={2}>
          <Box mb={0.5}>
            <Typography variant="h6">
              <b>{instantWin?.title}</b>
            </Typography>
          </Box>
          <Typography variant="body2">{instantWin?.description}</Typography>
        </Box>
        <Box
          style={{ width: "60%" }}
          display="flex"
          alignItems="center"
          mt={3}
          mb={2}
        >
          <Button
            className={classes.button}
            style={{ border: "1px solid black" }}
          >
            <Typography
              variant="body1"
              style={{ textTransform: "capitalize" }}
            >
              <b>
                {getLang(
                  lang,
                  isJoinStart ? "label.PLAY_NOW" : "label.EXPLORE_NOW"
                )}
              </b>
            </Typography>
          </Button>
        </Box>
        <Box className={classes.bannerDefaultImg}>
          <img src={InstantWinDefaultBanner} alt="ld" />
        </Box>
        {showCountdown && (!isJoinStart || !isJoinEnd) && (
          <Box className={classes.defaultThemeBannerCountdown}>
            <Typography variant="caption">
              {isJoinStart
                ? getLang(lang, "label.ENDS_IN")
                : getLang(lang, "label.STARTS_IN")}{" "}
              <span>
                <b>
                  {cd.day}:{formatTwoDigits(cd.hour)}:
                  {formatTwoDigits(cd.min)}:{formatTwoDigits(cd.sec)}
                </b>
              </span>
            </Typography>
          </Box>
        )}
        {isEnded && (
          <Box className={classes.overlay}>
            <Typography variant="body1">
              {getLang(lang, "label.ENDED")}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Image banner
  return (
    <Box
      className={classes.bannerContainer}
    >
      <img
        src={instantWin.image_url}
        alt="instant-win-img"
        className={classes.bannerImg}
      />
      {showCountdown && (!isJoinStart || !isJoinEnd) && (
        <Box className={classes.bannerCountdown}>
          <Typography variant="caption">
            {isJoinStart
              ? getLang(lang, "label.ENDS_IN")
              : getLang(lang, "label.STARTS_IN")}{" "}
            <span>
              <b>
                {cd.day}:{formatTwoDigits(cd.hour)}:{formatTwoDigits(cd.min)}:
                {formatTwoDigits(cd.sec)}
              </b>
            </span>
          </Typography>
        </Box>
      )}
      <Box mt={1}>
        <Box display="flex" alignItems="center" mb={0.5} flexWrap="wrap">
          <Box mr={1} mt={0.5}>
            <Typography variant="body1">
              <b>{instantWin.title}</b>
            </Typography>
          </Box>
          <Box className={classes.label} mt={0.5}>
            <Typography variant="body2">
              <b>{getLang(lang, "label.INSTANT_WIN")}</b>
            </Typography>
          </Box>
        </Box>
        <Typography variant="body1">{instantWin.description}</Typography>
      </Box>
      <Box mt={2} mb={1}>
        <Button
          className={classes.button}
          style={{ border: "1px solid black" }}
        >
          <Typography variant="body1" style={{ textTransform: "capitalize" }}>
            <b>
              {getLang(
                lang,
                isJoinStart ? "label.PLAY_NOW" : "label.EXPLORE_NOW"
              )}
            </b>
          </Typography>
        </Button>
      </Box>
      {isEnded && (
        <Box className={classes.overlay}>
          <Typography variant="body1">
            {getLang(lang, "label.ENDED")}
          </Typography>
        </Box>
        )}
    </Box>
  );
};

  const currentCampaign = total > 0 ? campaigns[currentIndex] : null;
  const currentCountdown =
    currentCampaign && countdowns[currentCampaign.id]
      ? countdowns[currentCampaign.id]
      : { day: 0, hour: 0, min: 0, sec: 0, status: "ended" };

  return (
    <Box p={2}>
      <Box className={classes.carouselWrapper}>
        <Box className={classes.container}>
        {/* Left Arrow */}
        {total > 1 && (
          <IconButton
            className={`${classes.arrowButton} ${classes.leftArrow}`}
            onClick={handlePrevious}
            disabled={isLoading}
          >
            <ChevronLeft />
          </IconButton>
        )}

        {/* Main Content */}
        <Box className={classes.container}>
          <Box style={{ width: "100%" }}>
            {isLoading ? (
              <Box
                p={2}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                style={{ width: "100%" }}
              >
                <Skeleton width={"100%"} height={30} />
                <Skeleton width={"100%"} height={30} />
                <Skeleton width={"100%"} height={30} />
                <Skeleton width={"100%"} height={30} />
              </Box>
            ) : total > 0 ? (
              renderCampaign(currentCampaign, currentCountdown)
            ) : null}
          </Box>
        </Box>

        {/* Right Arrow */}
        {total > 1 && (
          <IconButton
            className={`${classes.arrowButton} ${classes.rightArrow}`}
            onClick={handleNext}
            disabled={isLoading}
          >
            <ChevronRight />
          </IconButton>
        )}
        </Box>
      </Box>

      {/* Indicators */}
      {total > 1 && !isLoading && (
        <Box className={classes.indicators}>
          {campaigns.map((c, index) => (
            <div
              key={c.id || index}
              className={`${classes.indicator} ${
                index === currentIndex ? "active" : ""
              }`}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}